import type { LocalPrintJob, PrintCopiesMode, PrintJobKind } from "@shared/contracts";

export type PaperWidth = 58 | 80;
export type PrinterTransport = "web-serial" | "local-bridge" | "bluetooth" | "usb";

export type NamedPrinterDeviceProfile = {
  id: string;
  label: string;
  transport: PrinterTransport;
  paperWidth: PaperWidth;
  bridgeEndpoint?: string;
  deviceAddress?: string;
  enabled: boolean;
};

export type BridgeLifecycleStatus = {
  installed: boolean;
  version: string | null;
  minimumVersion: string;
  compatible: boolean;
  paired: boolean;
  connected: boolean;
  state: "uninstalled" | "unpaired" | "disconnected" | "connected" | "incompatible";
  lastError?: string;
};

export type BridgeDeviceTransition = "pair" | "reconnect";

export function validateNamedPrinterDeviceProfile(profile: NamedPrinterDeviceProfile): void {
  if (!/^[a-zA-Z0-9._-]{2,64}$/.test(profile.id)) throw new Error("Printer profile ID must contain 2 to 64 letters, numbers, dots, underscores or hyphens");
  if (!profile.label.trim()) throw new Error("Printer profile label is required");
  if (!["web-serial", "local-bridge", "bluetooth", "usb"].includes(profile.transport)) throw new Error("Printer profile transport is invalid");
  if (![58, 80].includes(profile.paperWidth)) throw new Error("Printer profile paper width is invalid");
  if ((profile.transport === "bluetooth" || profile.transport === "usb") && !profile.deviceAddress?.trim()) throw new Error("Bridge printer device address is required");
  if (profile.bridgeEndpoint && !/^https?:\/\//.test(profile.bridgeEndpoint)) throw new Error("Bridge endpoint must be an HTTP(S) URL");
}

function versionParts(version: string): number[] {
  return version.split(".").map((part) => Number.parseInt(part, 10)).map((part) => Number.isFinite(part) ? part : 0);
}

export function isBridgeVersionCompatible(version: string | null, minimumVersion = "1.0.0"): boolean {
  if (!version) return false;
  const current = versionParts(version);
  const minimum = versionParts(minimumVersion);
  for (let index = 0; index < Math.max(current.length, minimum.length); index += 1) {
    const difference = (current[index] ?? 0) - (minimum[index] ?? 0);
    if (difference !== 0) return difference > 0;
  }
  return true;
}

export function bridgeLifecycleStatus(input: { installed: boolean; version?: string | null; paired: boolean; connected: boolean; minimumVersion?: string; lastError?: string }): BridgeLifecycleStatus {
  const minimumVersion = input.minimumVersion ?? "1.0.0";
  const version = input.version ?? null;
  const compatible = input.installed && isBridgeVersionCompatible(version, minimumVersion);
  const state = !input.installed ? "uninstalled" : !compatible ? "incompatible" : !input.paired ? "unpaired" : !input.connected ? "disconnected" : "connected";
  return { installed: input.installed, version, minimumVersion, compatible, paired: input.paired, connected: input.connected, state, ...(input.lastError ? { lastError: input.lastError } : {}) };
}

export function nextBridgeDeviceTransition(current: BridgeLifecycleStatus, transition: BridgeDeviceTransition, result: { ok: boolean; error?: string }): BridgeLifecycleStatus {
  if (!current.compatible) return { ...current, state: current.installed ? "incompatible" : "uninstalled", lastError: "A compatible local bridge is required" };
  if (transition === "pair") return result.ok ? { ...current, paired: true, connected: true, state: "connected", lastError: undefined } : { ...current, state: "unpaired", lastError: result.error ?? "Printer pairing failed" };
  return result.ok && current.paired ? { ...current, connected: true, state: "connected", lastError: undefined } : { ...current, connected: false, state: "disconnected", lastError: result.error ?? "Printer reconnect failed" };
}

export type PrinterFormatProfile = {
  paperWidth: PaperWidth;
  printWidth: 29 | 43;
  printWidthDouble: 15 | 22;
  dateFormat: "short" | "full";
  codePage: 6;
  tailingSpace: 60;
};

const PRINTER_PROFILES: Record<PaperWidth, PrinterFormatProfile> = {
  58: { paperWidth: 58, printWidth: 29, printWidthDouble: 15, dateFormat: "short", codePage: 6, tailingSpace: 60 },
  80: { paperWidth: 80, printWidth: 43, printWidthDouble: 22, dateFormat: "full", codePage: 6, tailingSpace: 60 },
};

export function getPrinterFormatProfile(paperWidth: PaperWidth): PrinterFormatProfile {
  return { ...PRINTER_PROFILES[paperWidth] };
}

export function printKindsForMode(mode: PrintCopiesMode): PrintJobKind[] {
  if (mode === "none") return [];
  if (mode === "kitchen") return ["kitchen"];
  return ["kitchen", "receipt"];
}

export function createPrintJobs(input: {
  orderId: string;
  createdAt: string;
  paperWidth: PaperWidth;
  mode: PrintCopiesMode;
  idFactory: (kind: PrintJobKind) => string;
  language?: "en" | "zh-Hant" | "zh-Hans";
  routes?: Partial<Record<PrintJobKind, "web-serial" | "local-bridge" | "bluetooth" | "usb">>;
}): LocalPrintJob[] {
  return printKindsForMode(input.mode).map((kind) => ({
    id: input.idFactory(kind),
    orderId: input.orderId,
    kind,
    status: "pending",
    paperWidth: input.paperWidth,
    createdAt: input.createdAt,
    attemptCount: 0,
    language: input.language,
    transport: input.routes?.[kind],
  }));
}

export type PrintRouteConfig = {
  receipt: "none" | "web-serial" | "local-bridge" | "bluetooth" | "usb";
  kitchen: "none" | "web-serial" | "local-bridge" | "bluetooth" | "usb";
};

export function routeForPrintKind(kind: PrintJobKind, routes: PrintRouteConfig): PrintRouteConfig["receipt"] {
  return kind === "kitchen" ? routes.kitchen : routes.receipt;
}

export type HardwareTransportPath = {
  transport: PrinterTransport;
  integration: "browser-web-serial" | "localhost-bridge";
  directBrowserAccess: boolean;
  note: string;
};

export function hardwareTransportPath(transport: PrinterTransport): HardwareTransportPath {
  if (transport === "web-serial") return { transport, integration: "browser-web-serial", directBrowserAccess: true, note: "Requires a user gesture and a Web Serial-capable browser." };
  if (transport === "local-bridge") return { transport, integration: "localhost-bridge", directBrowserAccess: false, note: "The local bridge owns OS printer discovery and command delivery." };
  return { transport, integration: "localhost-bridge", directBrowserAccess: false, note: `The ${transport.toUpperCase()} printer path is provided by the local bridge; no direct browser pairing is claimed.` };
}

export function createReprintJob(original: LocalPrintJob, reprintJobId: string, createdAt: string): LocalPrintJob {
  if (!reprintJobId || reprintJobId === original.id) throw new Error("A reprint must have a distinct job identity");
  return {
    ...original,
    id: reprintJobId,
    status: "pending",
    createdAt,
    attemptCount: 0,
    lastError: undefined,
    reprintOf: original.id,
  };
}

export type HardwareCapabilities = {
  webSerial: boolean;
  localBridge: boolean;
  bluetooth: boolean;
  usb: boolean;
  cashDrawer: boolean;
  cutter: boolean;
};

export type HardwareStatus = {
  configuredTransport: PrinterTransport | "none";
  state: "ready" | "unavailable" | "unsupported";
  capabilities: HardwareCapabilities;
  reason: string;
};

export function evaluateHardware(input: {
  configuredTransport: PrinterTransport | "none";
  capabilities: HardwareCapabilities;
}): HardwareStatus {
  if (input.configuredTransport === "none") {
    return { configuredTransport: "none", state: "unsupported", capabilities: input.capabilities, reason: "No printer transport is configured" };
  }
  const available = input.configuredTransport === "web-serial"
    ? input.capabilities.webSerial
    : input.configuredTransport === "local-bridge"
      ? input.capabilities.localBridge
      : input.configuredTransport === "bluetooth"
        ? input.capabilities.bluetooth
        : input.capabilities.usb;
  return {
    configuredTransport: input.configuredTransport,
    state: available ? "ready" : "unavailable",
    capabilities: input.capabilities,
    reason: available ? "Configured transport is available" : "Configured transport is unavailable on this device",
  };
}
